import { useState, useRef, useCallback, useEffect } from 'react';
import RecordRTC from 'recordrtc';
import { DeviceCheckResult } from 'components/MediaDevicesCheck';
import { mediaStreamStore } from 'stores/mediaStreamStore';

export type VideoQuality = 'high' | 'medium' | 'low';

// Маппинг качества в битрейт (бит/с)
const VIDEO_BITRATE_MAP: Record<VideoQuality, number> = {
  high: 2500000, // 2.5 Мбит/с - высокое качество
  medium: 1500000, // 1.5 Мбит/с - среднее качество (баланс качества и размера)
  low: 1000000 // 1 Мбит/с - низкое качество (максимальное сжатие)
};

const getVideoBitrate = (quality: VideoQuality = 'medium'): number => {
  return VIDEO_BITRATE_MAP[quality];
};

interface UseVideoRecordingOptions {
  chunkInterval?: number;
  onChunkReady?: (chunk: Blob) => Promise<void> | void;
  videoQuality?: VideoQuality;
}

interface UseVideoRecordingResult {
  isRecording: boolean;
  recordingTime: number;
  hasVideoAccess: boolean;
  videoRef: React.RefObject<HTMLVideoElement>;
  recordedBlob: Blob | null;
  previewUrl: string | null;
  // Devices check (объединенный функционал)
  devicesResult: DeviceCheckResult | null;
  isCheckingDevices: boolean;
  devicesError: string | null;
  clearDevicesError: () => void;
  checkDevices: () => Promise<void>;
  isDevicesReady: boolean;
  // Recording methods
  startRecording: (selectedMicrophone?: string, selectedCamera?: string) => Promise<void>;
  stopRecording: () => Promise<void>;
  resetRecording: () => void;
}

export const useVideoRecording = (options?: UseVideoRecordingOptions): UseVideoRecordingResult => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [hasVideoAccess, setHasVideoAccess] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // Devices check states (объединенный функционал)
  const [devicesResult, setDevicesResult] = useState<DeviceCheckResult | null>(null);
  const [isCheckingDevices, setIsCheckingDevices] = useState(false);
  const [devicesError, setDevicesError] = useState<string | null>(null);

  const recorderRef = useRef<RecordRTC | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const timerRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunkCallbackRef = useRef<UseVideoRecordingOptions['onChunkReady']>(options?.onChunkReady);
  const chunkIntervalRef = useRef<number>(options?.chunkInterval ?? 0);
  const chunkQueueRef = useRef<Promise<void>>(Promise.resolve());
  const isRecordingRef = useRef(false);
  const recorderDestroyedRef = useRef(false);
  const trackEndedHandlersRef = useRef<Array<() => void>>([]);
  
  const cleanupTrackListeners = useCallback(() => {
    trackEndedHandlersRef.current.forEach((cleanup) => cleanup());
    trackEndedHandlersRef.current = [];
  }, []);

  const destroyRecorderInstance = useCallback((recorder: RecordRTC | null) => {
    if (!recorder) {
      return;
    }
    if (recorderDestroyedRef.current) {
      return;
    }
    const mediaRecorder = (recorder as unknown as { mediaRecorder?: MediaRecorder | null })?.mediaRecorder;
    if (!mediaRecorder) {
      recorderDestroyedRef.current = true;
      return;
    }
    if (typeof (recorder as unknown as { destroy?: () => void }).destroy === 'function') {
      try {
        (recorder as unknown as { destroy: () => void }).destroy();
      } catch (error) {
        console.warn('Не удалось завершить запись корректно:', error);
      }
    }
    recorderDestroyedRef.current = true;
  }, []);

  useEffect(() => {
    chunkCallbackRef.current = options?.onChunkReady;
  }, [options?.onChunkReady]);

  useEffect(() => {
    chunkIntervalRef.current = options?.chunkInterval ?? 0;
  }, [options?.chunkInterval]);

  // Таймер записи
  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    if (isRecording) {
      timerRef.current = window.setInterval(() => {
        setRecordingTime((prevTime) => prevTime + 1);
      }, 1000);
    } else {
      setRecordingTime(0);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isRecording]);

  // Очистка URL
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Функция проверки устройств (объединенный функционал из useMediaDevices)
  const checkDevices = useCallback(async () => {
    setIsCheckingDevices(true);
    setDevicesError(null);

    try {
      // Проверяем поддержку MediaDevices API
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        throw new Error('MediaDevices API не поддерживается в вашем браузере');
      }

      const result: DeviceCheckResult = {
        microphone: {
          available: false,
          devices: [],
          hasPermission: false
        },
        camera: {
          available: false,
          devices: [],
          hasPermission: false
        }
      };

      // Получаем список всех устройств
      const devices = await navigator.mediaDevices.enumerateDevices();

      // Фильтруем микрофоны и камеры
      const microphones = devices.filter((device) => device.kind === 'audioinput');
      const cameras = devices.filter((device) => device.kind === 'videoinput');

      result.microphone.available = microphones.length > 0;
      result.microphone.devices = microphones;
      result.camera.available = cameras.length > 0;
      result.camera.devices = cameras;

      // Проверяем разрешения для микрофона
      try {
        const audioConstraintsVariants: MediaStreamConstraints[] = [
          { audio: true },
          {
            audio: {
              echoCancellation: false,
              noiseSuppression: false,
              autoGainControl: false
            }
          }
        ];

        if (microphones.length > 0) {
          microphones.forEach((device) => {
            audioConstraintsVariants.push({
              audio: {
                deviceId: device.deviceId ? { exact: device.deviceId } : undefined,
                echoCancellation: false,
                noiseSuppression: false,
                autoGainControl: false
              }
            } as MediaStreamConstraints);
          });
        }

        let micStream: MediaStream | null = null;
        try {
          // Пробуем получить стрим из store
          const currentStream = mediaStreamStore.getCurrentStream();
          if (currentStream && currentStream.getAudioTracks().length > 0) {
            micStream = currentStream;
            mediaStreamStore.addReference();
          } else {
            // Создаем временный стрим для проверки
            micStream = await mediaStreamStore.getAudioStream();
            mediaStreamStore.addReference();
          }
        } catch (e) {
          const lastError = e;
          const message =
            (lastError as DOMException)?.name === 'NotReadableError'
              ? 'Микрофон занят другой программой. Закройте приложения, использующие микрофон (Zoom/Meet/Skype/OBS) и попробуйте снова.'
              : lastError instanceof Error
              ? lastError.message
              : 'Не удалось получить доступ к микрофону';
          throw new Error(message);
        }

        if (micStream) {
          result.microphone.hasPermission = true;
        }

        // Освобождаем ссылку на стрим
        if (micStream) {
          mediaStreamStore.removeReference();
        }
      } catch (err) {
        result.microphone.hasPermission = false;
      }

      // Проверяем разрешения для камеры
      try {
        const videoConstraints = [
          {
            video: {
              width: { ideal: 1920, min: 1280 },
              height: { ideal: 1080, min: 720 },
              frameRate: { ideal: 30, min: 25 }
            }
          },
          {
            video: {
              width: { ideal: 1280, min: 1024 },
              height: { ideal: 720, min: 576 },
              frameRate: { ideal: 30, min: 20 }
            }
          },
          {
            video: {
              width: { ideal: 1280 },
              height: { ideal: 720 },
              frameRate: { ideal: 30 }
            }
          },
          {
            video: true
          }
        ];

        let videoStream: MediaStream | null = null;
        let bestSettings: MediaTrackSettings | null = null;

        // Пробуем получить стрим через store
        try {
          // Сначала проверяем, есть ли уже стрим с видео
          const currentStream = mediaStreamStore.getCurrentStream();
          if (currentStream && currentStream.getVideoTracks().length > 0) {
            videoStream = currentStream;
            mediaStreamStore.addReference();
            const videoTrack = videoStream.getVideoTracks()[0];
            if (videoTrack) {
              bestSettings = videoTrack.getSettings();
            }
          } else {
            // Создаем временный стрим для проверки
            for (const constraints of videoConstraints) {
              try {
                videoStream = await mediaStreamStore.getStream({
                  video: constraints.video,
                  audio: false
                });
                const videoTrack = videoStream.getVideoTracks()[0];
                if (videoTrack) {
                  const settings = videoTrack.getSettings();
                  if (
                    !bestSettings ||
                    (settings.width &&
                      settings.height &&
                      settings.width * settings.height >
                        (bestSettings.width || 0) * (bestSettings.height || 0))
                  ) {
                    bestSettings = settings;
                    if (
                      settings.width &&
                      settings.height &&
                      settings.width * settings.height >= 1280 * 720
                    ) {
                      break;
                    }
                  }
                }
              } catch (err) {
                continue;
              }
            }
          }

          if (videoStream && bestSettings) {
            result.camera.hasPermission = true;
            const width = bestSettings.width || 0;
            const height = bestSettings.height || 0;
            const frameRate = bestSettings.frameRate || 0;

            result.camera.resolution = { width, height };
            result.camera.fps = frameRate;

            // Освобождаем ссылку на стрим после проверки
            mediaStreamStore.removeReference();
          } else {
            throw new Error('Не удалось получить доступ к камере');
          }
        } catch (err) {
          if (videoStream) {
            mediaStreamStore.removeReference();
          }
          throw err;
        }
      } catch (err) {
        result.camera.hasPermission = false;
      }

      setDevicesResult(result);
    } catch (err) {
      setDevicesError(
        err instanceof Error ? err.message : 'Произошла ошибка при проверке устройств'
      );
    } finally {
      setIsCheckingDevices(false);
    }
  }, []);

  const clearDevicesError = useCallback(() => {
    setDevicesError(null);
  }, []);

  // Автоматическая проверка доступа к видео при монтировании (упрощенная)
  useEffect(() => {
    const checkVideoAccess = async () => {
      try {
        const selectedCamera = localStorage.getItem('selectedCamera') || undefined;
        const selectedMicrophone = localStorage.getItem('selectedMicrophone') || undefined;

        const stream = await mediaStreamStore.getStream({
          video: { width: 1280, height: 720 },
          audio: true,
          selectedCamera,
          selectedMicrophone
        });
        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = null;
          videoRef.current.srcObject = stream;
          setHasVideoAccess(true);
          setTimeout(() => {
            videoRef.current?.play().catch(console.error);
          }, 100);
        }
      } catch (error) {
        console.warn('Не удалось получить доступ к видео:', error);
        setHasVideoAccess(false);
      }
    };

    checkVideoAccess();
    const currentVideoElement = videoRef.current;

    return () => {
      if (streamRef.current) {
        mediaStreamStore.removeReference();
        streamRef.current = null;
      }
      if (currentVideoElement) {
        currentVideoElement.srcObject = null;
      }
    };
  }, []);

  const handleChunk = useCallback((chunk: Blob) => {
    if (!isRecordingRef.current) {
      return;
    }
    if (!chunk || chunk.size === 0) {
      return;
    }

    const callback = chunkCallbackRef.current;
    if (!callback) {
      return;
    }

    chunkQueueRef.current = chunkQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        if (!isRecordingRef.current) {
          return;
        }
        const activeCallback = chunkCallbackRef.current;
        if (!activeCallback) {
          return;
        }
        await activeCallback(chunk);
      })
      .catch((error) => {
        console.error('Ошибка отправки видео чанка:', error);
      });
  }, []);

  const startRecording = useCallback(
    async (selectedMicrophone?: string, selectedCamera?: string) => {
      try {
        // Очищаем предыдущий результат
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
        }
        setRecordedBlob(null);

        let camera: MediaStream | undefined;

        // Получаем поток через глобальный store
        try {
          camera = await mediaStreamStore.getStream({
            video: { width: 1280, height: 720 },
            audio: true,
            selectedCamera,
            selectedMicrophone
          });
        } catch (error) {
          // Если не удалось получить с указанными устройствами, пробуем без них
          if (selectedCamera || selectedMicrophone) {
            try {
              camera = await mediaStreamStore.getStream({
                video: { width: 1280, height: 720 },
                audio: true
              });
            } catch (fallbackError) {
              const errorMessage =
                error instanceof Error
                  ? error.message
                  : 'Не удалось получить доступ к микрофону или камере';
              throw new Error(errorMessage);
            }
          } else {
            // Используем существующий поток из store
            const currentStream = mediaStreamStore.getCurrentStream();
            if (currentStream) {
              camera = currentStream;
              mediaStreamStore.addReference();
            } else {
              throw new Error('Поток не инициализирован');
            }
          }
        }

        // Показываем видео. Сначала null — на iOS старый srcObject может давать серый экран
        if (videoRef.current) {
          videoRef.current.srcObject = null;
          videoRef.current.srcObject = camera;
          setHasVideoAccess(true);
          setTimeout(() => {
            videoRef.current?.play().catch(console.error);
          }, 100);
        }

        // Создаем RecordRTC с форматом webm
        const timeSlice = chunkIntervalRef.current > 0 ? chunkIntervalRef.current : undefined;
        const videoQuality = options?.videoQuality || 'medium';
        const videoBitrate = getVideoBitrate(videoQuality);
        const recorder = new RecordRTC(camera, {
          type: 'video',
          mimeType: 'video/webm',
          videoBitsPerSecond: videoBitrate,
          timeSlice,
          ondataavailable: handleChunk
        });
        recorderRef.current = recorder;
        chunkQueueRef.current = Promise.resolve();
        recorderDestroyedRef.current = false;

        // Начинаем запись
        recorder.startRecording();
        setIsRecording(true);
        isRecordingRef.current = true;

        // Слушаем потерю треков (браузер усыпил вкладку, устройство отключено)
        cleanupTrackListeners();
        camera.getTracks().forEach((track) => {
          const handler = () => {
            if (isRecordingRef.current) {
              console.warn(`Трек ${track.kind} завершился во время записи`);
              setDevicesError(
                'Камера или микрофон были отключены. Пожалуйста, нажмите "Стоп" и начните запись заново.'
              );
            }
          };
          track.addEventListener('ended', handler);
          trackEndedHandlersRef.current.push(() => track.removeEventListener('ended', handler));
        });

        // Сохраняем камеру для освобождения
        (recorder as any).camera = camera;
      } catch (error) {
        console.error('Ошибка при запуске записи:', error);
        setHasVideoAccess(false);

        // Создаем улучшенное сообщение об ошибке
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Произошла ошибка при запуске записи. Попробуйте обновить страницу.';

        // Обновляем состояние ошибки устройств для отображения пользователю
        setDevicesError(errorMessage);

        // Пробрасываем ошибку дальше
        throw error instanceof Error ? error : new Error(errorMessage);
      }
    },
    [previewUrl, handleChunk, options?.videoQuality, cleanupTrackListeners]
  );

  const stopRecording = useCallback(async () => {
    if (recorderRef.current && isRecording) {
      const recorder = recorderRef.current;

      // Callback для остановки (как в примере)
      const stopRecordingCallback = () => {
        if (videoRef.current) {
          videoRef.current.srcObject = null;
          videoRef.current.src = URL.createObjectURL(recorder.getBlob());
        }

        // Освобождаем ссылку на стрим в store
        if ((recorder as any).camera) {
          mediaStreamStore.removeReference();
        }

        // Сохраняем результат
        const blob = recorder.getBlob();
        setRecordedBlob(blob);
        setPreviewUrl(URL.createObjectURL(blob));
        destroyRecorderInstance(recorder);
        recorderRef.current = null;

        // Сбрасываем доступ к видео
        setHasVideoAccess(false);
      };

      // Останавливаем запись - это вызовет ondataavailable с последним чанком
      cleanupTrackListeners();
      recorder.stopRecording(stopRecordingCallback);
      setIsRecording(false);
      
      // Даем время последнему чанку попасть в очередь
      // RecordRTC вызывает ondataavailable асинхронно, поэтому нужна небольшая задержка
      await new Promise((resolve) => setTimeout(resolve, 100));
      
      // Ждем завершения отправки всех чанков (включая последний)
      // Последний чанк будет добавлен в очередь через handleChunk после stopRecording
      await chunkQueueRef.current.catch(() => undefined);
      
      // Устанавливаем флаг после завершения отправки всех чанков
      isRecordingRef.current = false;
    }
  }, [destroyRecorderInstance, isRecording, cleanupTrackListeners]);

  const resetRecording = useCallback(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setRecordedBlob(null);
    setRecordingTime(0);

    // Очищаем видео элемент
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      videoRef.current.src = '';
    }

    if (recorderRef.current) {
      recorderRef.current.destroy();
      recorderRef.current = null;
    }

    // Сбрасываем доступ к видео
    setHasVideoAccess(false);
  }, [previewUrl]);

  const isDevicesReady = !!(
    devicesResult &&
    devicesResult.microphone.hasPermission &&
    devicesResult.camera.hasPermission
  );

  useEffect(() => {
    return () => {
      cleanupTrackListeners();
      chunkCallbackRef.current = undefined;
      isRecordingRef.current = false;
      chunkQueueRef.current = Promise.resolve();
      if (recorderRef.current) {
        try {
          const recorder = recorderRef.current;
          if (typeof recorder.stopRecording === 'function') {
            try {
              recorder.stopRecording(() => {
                destroyRecorderInstance(recorder);
              });
            } catch (error) {
              console.warn('Не удалось корректно остановить запись при размонтировании:', error);
            }
          } else {
            destroyRecorderInstance(recorder);
          }
        } catch (error) {
          console.error('Ошибка очистки записи при размонтировании:', error);
        } finally {
          destroyRecorderInstance(recorderRef.current);
          recorderRef.current = null;
        }
      }
      if (streamRef.current) {
        mediaStreamStore.removeReference();
        streamRef.current = null;
      }
    };
  }, [destroyRecorderInstance, cleanupTrackListeners]);

  return {
    isRecording,
    recordingTime,
    hasVideoAccess,
    videoRef,
    recordedBlob,
    previewUrl,
    // Devices check
    devicesResult,
    isCheckingDevices,
    devicesError,
    clearDevicesError,
    checkDevices,
    isDevicesReady,
    // Recording methods
    startRecording,
    stopRecording,
    resetRecording
  };
};
